import React from "react";
import { FieldValues } from "react-hook-form";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "../../primitives/tabs";
import { CrudDrawer } from "./CrudDrawer";
import {
  TabbedCrudDrawerProps,
  TabbedCrudDrawerTab,
  TabbedCrudDrawerTabContext
} from "./TabbedCrudDrawer.types";

export type {
  TabbedCrudDrawerProps,
  TabbedCrudDrawerTab,
  TabbedCrudDrawerTabContext
};

export function TabbedCrudDrawer<TForm extends FieldValues = FieldValues>({
  tabs,
  form,
  selectedTab: controlledSelectedTab,
  onTabChange,
  defaultTab,
  header,
  onDelete,
  submitLabel: defaultSubmitLabel,
  cancelLabel: defaultCancelLabel,
  ...drawerProps
}: TabbedCrudDrawerProps<TForm>) {
  // Filter visible tabs
  const visibleTabs = React.useMemo(
    () => tabs.filter((tab) => tab.isVisible !== false),
    [tabs]
  );

  // Internal state for uncontrolled mode
  const [internalSelectedTab, setInternalSelectedTab] = React.useState(
    defaultTab || visibleTabs[0]?.id || ""
  );

  // Use controlled or uncontrolled state
  const selectedTab = controlledSelectedTab ?? internalSelectedTab;
  const setSelectedTab = React.useCallback(
    (tabId: string) => {
      if (onTabChange) {
        onTabChange(tabId);
      } else {
        setInternalSelectedTab(tabId);
      }
    },
    [onTabChange]
  );

  // Get current tab configuration
  const currentTab = React.useMemo(
    () => visibleTabs.find((tab) => tab.id === selectedTab),
    [visibleTabs, selectedTab]
  );

  // Create tab context for render functions
  const tabContext: TabbedCrudDrawerTabContext<TForm> = React.useMemo(
    () => ({
      form,
      currentTabId: selectedTab,
      setTab: setSelectedTab
    }),
    [form, selectedTab, setSelectedTab]
  );

  // Determine visibility of buttons based on current tab
  const showSaveButton = currentTab?.showSaveButton !== false;
  const showCancelButton = currentTab?.showCancelButton !== false;
  const showDeleteButton = currentTab?.showDeleteButton !== false && !!onDelete;

  // Get labels for current tab
  const submitLabel = currentTab?.submitLabel ?? defaultSubmitLabel;
  const cancelLabel = currentTab?.cancelLabel ?? defaultCancelLabel;

  return (
    <CrudDrawer
      {...drawerProps}
      form={form}
      submitLabel={submitLabel}
      cancelLabel={cancelLabel}
      contentClassName="overflow-hidden"
      submitProps={{
        ...drawerProps.submitProps,
        className: showSaveButton
          ? drawerProps.submitProps?.className
          : "hidden"
      }}
      cancelProps={{
        ...drawerProps.cancelProps,
        className: showCancelButton
          ? drawerProps.cancelProps?.className
          : "hidden"
      }}
      onDelete={showDeleteButton ? onDelete : undefined}
    >
      <div className="absolute inset-0 flex flex-col">
        {/* Fixed header section */}
        {header && <div className="flex-shrink-0 px-6">{header}</div>}

        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="flex flex-col flex-1 min-h-0"
        >
          {/* Fixed tabs list */}
          <div className="flex-shrink-0 px-6">
            <TabsList aria-label="Drawer tabs" variant="settingsTabs">
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  variant="minimalistic"
                  value={tab.id}
                  type="button"
                >
                  {tab.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {visibleTabs.map((tab) => (
              <TabsContent key={tab.id} value={tab.id}>
                {selectedTab === tab.id && tab.render(tabContext)}
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </div>
    </CrudDrawer>
  );
}
